import { useState } from 'react';
import axios from 'axios';
import {
  Button, Modal, Row, Col, Typography,
  Alert, App as AntApp, Flex, Upload,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { API_URL } from '../../config';
import { fmtDataBr } from '../../utils/dataFmt';
import { BANCO_LABEL } from './types';
import { getToken, fmt } from './utils';

const { Text } = Typography;

// Modal "Importar arquivo OFX" — upload, parse no backend (idempotente via FITID),
// retorna sumário (banco, conta, período, novas/duplicadas, auto-classif).
//
// 13/10/2026: o servidor RECUSA (409) a fatura de cartão ('fatura_de_cartao') e
// o extrato do que já entra sozinho pela integração do banco — hoje, a conta
// corrente do Inter ('conta_integrada', 'conta_nao_reconhecida'). A recusa fica
// escrita DENTRO do modal (não num aviso que some): ela diz o que aconteceu e
// o caminho do que a pessoa queria fazer (em geral, a fatura do cartão). O
// texto de ajuda dizia "reimportar é seguro — duplicatas são ignoradas", o que
// só vale para o MESMO arquivo; foi assim que 197 lançamentos entraram em dobro.
const RECUSAS = ['conta_integrada', 'conta_nao_reconhecida', 'fatura_de_cartao'];
export default function ModalUploadOFX({ onFechar, onSucesso }: { onFechar: () => void; onSucesso: () => void }) {
  const { message } = AntApp.useApp();
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<any>(null);
  // Recusa ou falha do envio: fica na tela até a pessoa trocar o arquivo.
  const [erro, setErro] = useState<{ texto: string; recusa: boolean } | null>(null);

  async function enviar() {
    if (!arquivo) return;
    setEnviando(true); setResultado(null); setErro(null);
    try {
      const buf = await arquivo.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let bin = '';
      for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
      const arquivo_base64 = btoa(bin);
      const token = await getToken();
      const r = await axios.post(`${API_URL}/financeiro/ofx/upload`,
        { arquivo_base64, nome: arquivo.name },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setResultado(d);
      message.success('Importação concluída.');
    } catch (e: any) {
      // O corpo pode vir como objeto, como texto (integração antiga) ou não vir
      // (rede, sessão vencida): nada aqui pode lançar e deixar a falha muda.
      let dados: any = null;
      try {
        const corpo = e.response?.data;
        dados = typeof corpo?.body === 'string' ? JSON.parse(corpo.body) : corpo;
      } catch { dados = null; }
      const recusa = RECUSAS.includes(dados?.codigo);
      const doServidor = typeof dados?.error === 'string' ? dados.error : (typeof dados?.erro === 'string' ? dados.erro : '');
      // Erro 5xx sem explicação própria: frase em português, não "status code 500".
      const semExplicacao = !doServidor || (!dados?.codigo && (e.response?.status || 0) >= 500);
      setErro({
        texto: semExplicacao ? 'Não consegui importar este arquivo agora, e nada foi importado. Tente de novo em alguns minutos; se continuar, avise o administrador.' : doServidor,
        recusa,
      });
      if (!recusa) message.error('Não consegui importar o arquivo.');
    } finally { setEnviando(false); }
  }

  return (
    <Modal open onCancel={onFechar} footer={null} title="📤 Importar arquivo OFX" width={600}>
      {!resultado ? (
        <>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 14 }}>
            Baixe o extrato da <strong>conta corrente</strong> em formato <strong>OFX</strong> no internet banking
            (Itaú, SICOOB e outros). Enviar o mesmo arquivo duas vezes não duplica.
          </Text>
          <Alert
            type="info" showIcon style={{ marginBottom: 14 }}
            title="Duas coisas que não entram por aqui"
            description={(
              <Text style={{ fontSize: 12 }}>
                <strong>Conta corrente do Inter</strong>: já entra sozinha, de hora em hora, pela integração com o banco.<br />
                <strong>Fatura de cartão</strong>: Financeiro › Pagamentos › Cartões de crédito › Importar CSV.
              </Text>
            )}
          />
          <Upload.Dragger
            accept=".ofx,.OFX" multiple={false}
            beforeUpload={(file) => { setArquivo(file); setErro(null); return false; }}
            onRemove={() => { setArquivo(null); setErro(null); }}
            fileList={arquivo ? [{ uid: '1', name: arquivo.name, size: arquivo.size } as any] : []}
          >
            <p><UploadOutlined style={{ fontSize: 32, color: '#0930D0' }} /></p>
            <p>Clique ou arraste o arquivo OFX aqui</p>
          </Upload.Dragger>
          {erro && (
            <Alert
              type={erro.recusa ? 'warning' : 'error'} showIcon style={{ marginTop: 14 }}
              title={erro.recusa ? 'Este arquivo não foi importado' : 'Não consegui importar o arquivo'}
              description={<Text style={{ fontSize: 12, whiteSpace: 'pre-line' }}>{erro.texto}</Text>}
            />
          )}
          <Flex justify="flex-end" gap={8} style={{ marginTop: 16 }}>
            <Button onClick={onFechar}>Cancelar</Button>
            <Button type="primary" icon={<UploadOutlined />} disabled={!arquivo} loading={enviando} onClick={enviar}
              style={{ background: '#0a7f4a', borderColor: '#0a7f4a' }}>
              Importar
            </Button>
          </Flex>
        </>
      ) : (
        <div>
          <Alert type="success" title="Importação concluída" showIcon style={{ marginBottom: 14 }} />
          <Row gutter={[8, 6]} style={{ fontSize: 12 }}>
            <Col span={10}><Text type="secondary">Banco</Text></Col>
            <Col span={14}><Text strong>{BANCO_LABEL[resultado.banco] || resultado.banco}</Text></Col>
            <Col span={10}><Text type="secondary">Conta</Text></Col>
            <Col span={14}>{resultado.conta_label}</Col>
            <Col span={10}><Text type="secondary">Período</Text></Col>
            <Col span={14}>
              {resultado.periodo?.inicio ? fmtDataBr(resultado.periodo.inicio) : '—'}
              {' a '}
              {resultado.periodo?.fim ? fmtDataBr(resultado.periodo.fim) : '—'}
            </Col>
            <Col span={10}><Text type="secondary">Saldo final</Text></Col>
            <Col span={14}><Text strong style={{ color: '#020D7F' }}>{resultado.saldo_final != null ? fmt(resultado.saldo_final) : '—'}</Text></Col>
            <Col span={24}><div style={{ borderTop: '0.5px dashed #eee', margin: '6px 0' }} /></Col>
            <Col span={10}><Text type="secondary">Transações no arquivo</Text></Col>
            <Col span={14}>{resultado.total_no_arquivo}</Col>
            <Col span={10}><Text style={{ color: '#0a7f4a' }}>✓ Novas inseridas</Text></Col>
            <Col span={14}><Text strong style={{ color: '#0a7f4a' }}>{resultado.inseridas}</Text></Col>
            <Col span={10}><Text type="secondary">↺ Duplicadas</Text></Col>
            <Col span={14}>{resultado.duplicadas}</Col>
            <Col span={10}><Text style={{ color: '#7C3AED' }}>🤖 Auto-classificadas</Text></Col>
            <Col span={14}><Text strong>{resultado.classificadas_por_regra}</Text></Col>
            {resultado.erros > 0 && (
              <>
                <Col span={10}><Text style={{ color: '#B91626' }}>⚠ Erros</Text></Col>
                <Col span={14}><Text strong style={{ color: '#B91626' }}>{resultado.erros}</Text></Col>
              </>
            )}
          </Row>
          <Alert
            type="info" showIcon style={{ marginTop: 16 }}
            title={<Text style={{ fontSize: 11 }}>💡 Próximo passo: classifique as transações <strong>Não classificadas</strong> manualmente. O sistema aprende regras a partir delas.</Text>}
          />
          <Button block type="primary" style={{ marginTop: 14, background: '#020D7F', borderColor: '#020D7F' }}
            onClick={onSucesso}>Ver extrato →</Button>
        </div>
      )}
    </Modal>
  );
}
